/**
 * Robot page — bot management dashboard.
 *
 * Shows active/stopped bots from /api/bot/list + event timeline for a
 * selected bot from /api/bot/{id}.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Slider,
  List,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  AddCircleOutline,
  SmartToy,
  StopCircle,
  TrendingUp,
  TrendingDown,
  AccessTime,
  Error as ErrorIcon,
  Refresh,
  FiberManualRecord,
  Warning,
  PlayArrow,
  Pause,
} from '@mui/icons-material';

import { useTradeStream, TradeEvent, PositionMark } from '../../hooks/useTradeStream';
import BotPnlChart, { MarkPoint } from './BotPnlChart';
import { buildTrailUpTo, TrailEvent } from './trail';
import UnderlyingQuote, { DayCondor, UnderlyingReplay } from './UnderlyingQuote';

/** A mark plus the local time we received it, for staleness dimming. */
export type LiveMark = PositionMark & { receivedAt: number };

/** Today's breadth-signal payload (BREADTH_SIGNAL / BREADTH_SPREAD_SIGNAL). */
export interface DayFees {
  amount: number;
  basis: 'actual' | 'estimated' | 'apportioned';
}

export interface TodaySignal {
  action?: string;
  breadth?: number;
  reds?: number;
  regimeGate?: { pass?: boolean; trailRate?: number; trailN?: number };
  redMap?: Record<string, boolean>;
  refPrice?: number;
  feed?: string;
  icEnteredToday?: boolean;
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export interface Bot {
  id: string;
  symbol: string;
  strategy: string;
  status: string;
  state: string;
  contractsTarget: number | null;
  currentOptionSymbol: string | null;
  currentStrike: number | null;
  currentExpiration: string | null;
  currentPremiumCollected: number | null;
  sharesHeld: number | null;
  costBasis: number | null;
  broker: string;
  /** Single-state bots (breadth dip/spread) carry their open position in
   *  config; the server surfaces it so the chip can read "Holding". */
  holding?: boolean;
  createdAt: string;
  updatedAt: string;
}

interface BotEvent {
  id: string;
  type: string;
  payload: Record<string, any> | null;
  createdAt: string;
}

const STATE_COLORS: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  OPEN_CALL: 'success',
  AWAITING_SHARES: 'info',
  ASSIGNED_PENDING_REBUY: 'warning',
  EXPIRED_PENDING_ROLL: 'warning',
  SELLING_PUTS: 'info',
  PUT_ASSIGNED: 'warning',
  CALLED_AWAY: 'info',
  IC_OPEN: 'success',
  IC_CLOSING: 'info',
  IC_PENDING_REENTRY: 'default',
  SLEEVE_SCANNING: 'default',
  DIP_SCANNING: 'default',
  SPREAD_SCANNING: 'default',
  PUT_SCANNING: 'default',
  TREND_SCANNING: 'default',
  BOOK_SCANNING: 'default',
  STOPPED: 'default',
  ERROR: 'error',
};

const STATE_LABELS: Record<string, string> = {
  OPEN_CALL: 'Open Call',
  AWAITING_SHARES: 'Buying Shares',
  ASSIGNED_PENDING_REBUY: 'Assigned — Rebuying',
  EXPIRED_PENDING_ROLL: 'Expired — Rolling',
  SELLING_PUTS: 'Selling Puts',
  PUT_ASSIGNED: 'Put Assigned',
  CALLED_AWAY: 'Called Away',
  IC_OPEN: 'IC Open',
  IC_CLOSING: 'IC Closing',
  IC_PENDING_REENTRY: 'Pending Re-entry',
  SLEEVE_SCANNING: 'Scanning',
  DIP_SCANNING: 'Scanning',
  SPREAD_SCANNING: 'Scanning',
  PUT_SCANNING: 'Scanning',
  TREND_SCANNING: 'Scanning',
  BOOK_SCANNING: 'Scanning',
  STOPPED: 'Stopped',
  ERROR: 'Error',
};

const STRATEGY_LABELS: Record<string, string> = {
  COVERED_CALL: 'Covered Call',
  WHEEL: 'Wheel',
  IRON_CONDOR: 'Iron Condor',
  LONG_CALL_SLEEVE: 'Long Call Sleeve',
  BREADTH_DIP: 'Breadth Dip',
  BREADTH_SPREAD: 'Breadth Spread',
  BREADTH_PUT: 'Breadth Put (Green)',
  STRADDLE_BOOK: 'Straddle Book',
  TREND_OPTION: 'Trend Option (0DTE)',
};

const BREADTH_STRATEGIES = new Set(['BREADTH_DIP', 'BREADTH_SPREAD', 'BREADTH_PUT']);
const MAX_SERIES_POINTS = 2200;

/** Today's date in ET (the trading calendar), as YYYY-MM-DD. */
const etToday = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());

/** Rough ET session check (Mon-Fri, 09:25-16:05 with grace margins) —
 * only gates DISPLAY affordances (live dot vs static replay), never
 * trading logic, so holidays erring "live" is harmless. */
const etMarketHours = () => {
  const now = new Date();
  const wd = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', weekday: 'short' }).format(now);
  if (wd === 'Sat' || wd === 'Sun') return false;
  const hm = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'America/New_York', hour12: false,
    hour: '2-digit', minute: '2-digit' }).format(now);
  return hm >= '09:25' && hm <= '16:05';
};

const EVENT_ICONS: Record<string, React.ReactNode> = {
  SETUP: <SmartToy fontSize="small" />,
  BUY_SHARES: <TrendingUp fontSize="small" />,
  SELL_CALL: <TrendingDown fontSize="small" />,
  ROLL: <Refresh fontSize="small" />,
  ASSIGNMENT_DETECTED: <ErrorIcon fontSize="small" />,
  EXPIRED: <AccessTime fontSize="small" />,
  NOTIFICATION_SENT: <SmartToy fontSize="small" />,
  STOPPED: <StopCircle fontSize="small" />,
  ERROR: <ErrorIcon fontSize="small" />,
  ORDER_EXPIRED: <AccessTime fontSize="small" />,
  NEEDS_ATTENTION: <Warning fontSize="small" />,
  NO_LIQUID_STRIKE: <Warning fontSize="small" />,
};

const Robot: React.FC = () => {
  const [bots, setBots] = useState<Bot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedBot, setSelectedBot] = useState<Bot | null>(null);
  const [events, setEvents] = useState<BotEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [stopping, setStopping] = useState<string | null>(null);
  // Stop is live-money and irreversible for the day: it closes legs, cancels
  // orders and clears `recurring`. One stray tap (10/25: an unexplained Stop
  // on the live IC at 18:25 ET on a Sunday) must not be enough — confirm first.
  const [confirmStop, setConfirmStop] = useState<Bot | null>(null);
  // Live position marks by botId, pushed by the server every manage tick.
  const [marks, setMarks] = useState<Record<string, LiveMark>>({});
  // Today's full mark series per bot (seeded from /marks, appended live).
  const [series, setSeries] = useState<Record<string, MarkPoint[]>>({});
  // Today's breadth-signal payload per bot.
  const [signals, setSignals] = useState<Record<string, TodaySignal | null>>({});
  // Realized P&L booked on the viewed day per bot (from exit/expiry events).
  const [realized, setRealized] = useState<Record<string, number | null>>({});
  // Regulatory fees for the day (live venue only; actual once the broker
  // archive has them, estimated same-day) — the card shows NET.
  const [fees, setFees] = useState<Record<string, DayFees | null>>({});
  // IC extras from /marks: the day's trail events and the cycle's strikes.
  const [trails, setTrails] = useState<Record<string, TrailEvent[]>>({});
  const [condors, setCondors] = useState<Record<string, DayCondor | null>>({});
  const [venue, setVenue] = useState<string | null>(null);
  // ET day whose charts are on screen. Today DURING MARKET HOURS = live
  // mode (marks append from the stream); a past day — or today once the
  // session is over — is a static replay from the DB. Before 9/16 today
  // stayed "live" all evening: the cards kept their live affordances
  // (pulse dot, holding heuristics) over a chart that could no longer
  // change, and the user reasonably asked why today couldn't be
  // replayed like any other finished day.
  const [viewDate, setViewDate] = useState<string>(etToday());
  const isToday = viewDate === etToday();
  const [sessionLive, setSessionLive] = useState<boolean>(etMarketHours());
  useEffect(() => {
    const t = setInterval(() => setSessionLive(etMarketHours()), 60_000);
    return () => clearInterval(t);
  }, []);
  // Live affordances only while the tape can actually move.
  const liveView = isToday && sessionLive;


  // Refs let the (stable) trade-stream handler read the latest state
  // without re-subscribing each render.
  const selectedBotIdRef = useRef<string | null>(null);
  selectedBotIdRef.current = selectedBot?.id ?? null;
  const isTodayRef = useRef(true);
  isTodayRef.current = isToday;
  const botsRef = useRef<Bot[]>([]);
  botsRef.current = bots;
  const lastHoldingRefetchRef = useRef(0);

  useEffect(() => {
    fetchBots();
    fetch(`${API_BASE}/api/trade/account`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((j) => setVenue(j.venue || null))
      .catch(() => {});
  }, []);

  // Seed each active bot's chart for the viewed day. Runs when the bot
  // LIST changes (mount, state-change refetch) and when the date selector
  // moves; on today, live points then append over the websocket.
  const seededRef = useRef<Set<string>>(new Set());
  const lastSeedDateRef = useRef<string>(viewDate);
  useEffect(() => {
    if (lastSeedDateRef.current !== viewDate) {
      // Date changed: drop the old day's data and reseed everything.
      lastSeedDateRef.current = viewDate;
      seededRef.current = new Set();
      setSeries({});
      setSignals({});
      setTrails({});
      setCondors({});
    }
    const toSeed = bots.filter(
      (b) => !['STOPPED', 'ERROR'].includes(b.state) && !seededRef.current.has(b.id),
    );
    toSeed.forEach((b) => seededRef.current.add(b.id));
    const forDate = viewDate;
    toSeed.forEach(async (b) => {
      try {
        const r = await fetch(
          `${API_BASE}/api/bot/${b.id}/marks?date=${forDate}`,
          { headers: authHeaders() },
        );
        const j = await r.json();
        if (r.ok && j.success && lastSeedDateRef.current === forDate) {
          setSeries((prev) => ({
            ...prev,
            [b.id]: [...(j.marks || []), ...(prev[b.id] || [])].slice(-MAX_SERIES_POINTS),
          }));
          if (j.todaySignal) {
            setSignals((prev) => ({ ...prev, [b.id]: j.todaySignal }));
          }
          setRealized((prev) => ({ ...prev, [b.id]: j.dayRealized ?? null }));
          setFees((prev) => ({ ...prev, [b.id]: j.dayFees ?? null }));
          setTrails((prev) => ({ ...prev, [b.id]: j.dayTrail || [] }));
          setCondors((prev) => ({ ...prev, [b.id]: j.dayCondor ?? null }));
        }
      } catch {
        /* chart seeds empty; live marks still append */
      }
    });
  }, [bots, viewDate]);

  const fetchBots = async () => {
    try {
      setLoading(true);
      setError('');
      const r = await fetch(`${API_BASE}/api/bot/list`, { headers: authHeaders() });
      const j = await r.json();
      if (r.ok && j.success) {
        setBots(j.bots || []);
      } else {
        setError(j.message || 'Failed to fetch bots');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  // Silent refetch — doesn't flip the top-level `loading` flag so the UI
  // doesn't flash a spinner on every live update.
  const refetchBotsQuietly = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/bot/list`, { headers: authHeaders() });
      const j = await r.json();
      if (r.ok && j.success) {
        setBots(j.bots || []);
      }
    } catch {
      /* ignore — next scheduled refetch will retry */
    }
  }, []);

  const fetchBotDetail = async (bot: Bot) => {
    setSelectedBot(bot);
    setLoadingEvents(true);
    try {
      const r = await fetch(`${API_BASE}/api/bot/${bot.id}`, { headers: authHeaders() });
      const j = await r.json();
      if (r.ok && j.success) {
        setEvents(j.events || []);
      }
    } catch (err) {
      console.error('Bot detail fetch error:', err);
    } finally {
      setLoadingEvents(false);
    }
  };

  // Quiet refetch of the event timeline when a live update lands for the
  // currently-selected bot.
  const refetchSelectedEvents = useCallback(async (botId: string) => {
    try {
      const r = await fetch(`${API_BASE}/api/bot/${botId}`, { headers: authHeaders() });
      const j = await r.json();
      if (r.ok && j.success) {
        setEvents(j.events || []);
      }
    } catch {
      /* ignore */
    }
  }, []);

  // IC extras arrive with /marks, which is seeded once per bot per day.
  // An entry or a trail arm that happens while the page is open would
  // otherwise only show after a reload: re-pull just the extras when a
  // live IC mark says the cycle has strikes we don't know, or a trail we
  // have no event for (at most once a minute per bot).
  const trailsRef = useRef(trails);
  trailsRef.current = trails;
  const condorsRef = useRef(condors);
  condorsRef.current = condors;
  const extrasPulledRef = useRef<Record<string, number>>({});
  const refreshIcExtras = useCallback(async (botId: string) => {
    const now = Date.now();
    if (now - (extrasPulledRef.current[botId] || 0) < 60_000) return;
    extrasPulledRef.current[botId] = now;
    try {
      const r = await fetch(`${API_BASE}/api/bot/${botId}/marks?date=${etToday()}`, { headers: authHeaders() });
      const j = await r.json();
      if (r.ok && j.success && isTodayRef.current) {
        setTrails((prev) => ({ ...prev, [botId]: j.dayTrail || [] }));
        setCondors((prev) => ({ ...prev, [botId]: j.dayCondor ?? null }));
      }
    } catch {
      /* the chart still derives the trail from the marks */
    }
  }, []);

  // --- Live bot/trade event stream -------------------------------------
  const handleTradeEvent = useCallback(
    (ev: TradeEvent) => {
      // account_synced fires on any Alpaca trade event — bot-originated
      // or manual — so the dashboard reflects the real position without
      // needing a page refresh.
      if (ev.type === 'account_synced') {
        refetchBotsQuietly();
        if (selectedBotIdRef.current) {
          refetchSelectedEvents(selectedBotIdRef.current);
        }
        return;
      }
      if (!ev.botId) return;
      // Live mark: update in place — no refetch, this is the high-
      // frequency path (every manage tick while a position is open).
      if (ev.type === 'position_mark') {
        const mark = ev as unknown as PositionMark;
        setMarks((prev) => ({
          ...prev,
          [mark.botId]: { ...mark, receivedAt: Date.now() },
        }));
        // A mark for a bot the list says is NOT holding means an entry
        // the list hasn't caught up with — entries book their trades
        // directly and never fire trade_filled (the 9/22 put launch:
        // the chart streamed live while the chip stayed "Scanning").
        // One quiet, debounced refetch flips the chip to "Holding"
        // within ~a second; exits keep their existing refetch path.
        const listed = botsRef.current.find((b) => b.id === mark.botId);
        if (
          listed && listed.holding !== true &&
          Date.now() - lastHoldingRefetchRef.current > 10_000
        ) {
          lastHoldingRefetchRef.current = Date.now();
          refetchBotsQuietly();
        }
        if (isTodayRef.current && mark.strategy === 'IRON_CONDOR') {
          const needCondor = !condorsRef.current[mark.botId];
          const needTrail = mark.trailArmed === true
            && !(trailsRef.current[mark.botId] || []).some((e) => e.type === 'TRAIL_ARMED');
          if (needCondor || needTrail) refreshIcExtras(mark.botId);
        }
        // Append to the chart only while viewing today — a past day is a
        // static replay and must not grow a live tail.
        if (isTodayRef.current) {
          setSeries((prev) => ({
            ...prev,
            [mark.botId]: [...(prev[mark.botId] || []), mark as unknown as MarkPoint]
              .slice(-MAX_SERIES_POINTS),
          }));
        }
        return;
      }
      // Any bot-state change → refresh the list so the pill + premium
      // cards update within ~1s of Alpaca's decision.
      if (
        ev.type === 'bot_state_changed' ||
        ev.type === 'bot_needs_attention' ||
        ev.type === 'trade_filled' ||
        ev.type === 'trade_terminal'
      ) {
        // A fill can be an exit: drop the bot from the seeded set so the
        // next list refresh re-pulls its marks + dayRealized and the card
        // flips from live P&L to the realized final number.
        if (ev.botId && isTodayRef.current) {
          seededRef.current.delete(ev.botId);
        }
        refetchBotsQuietly();
      }
      // If this event is for the bot whose timeline is currently visible,
      // refresh the events pane too.
      if (selectedBotIdRef.current && ev.botId === selectedBotIdRef.current) {
        refetchSelectedEvents(ev.botId);
      }
    },
    [refetchBotsQuietly, refetchSelectedEvents, refreshIcExtras],
  );

  const { status: streamStatus } = useTradeStream(handleTradeEvent);

  const handleStop = async (botId: string) => {
    setStopping(botId);
    try {
      const r = await fetch(`${API_BASE}/api/bot/${botId}/stop`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (r.ok) {
        await fetchBots();
        if (selectedBot?.id === botId) {
          setSelectedBot(null);
          setEvents([]);
        }
      }
    } catch (err) {
      console.error('Stop bot error:', err);
    } finally {
      setStopping(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const activeBots = bots.filter(b => !['STOPPED', 'ERROR'].includes(b.state));
  const inactiveBots = bots.filter(b => ['STOPPED', 'ERROR'].includes(b.state));

  return (
    <Box>
      {/* Header must WRAP: on a phone a single non-wrapping row (title
          + live dot + venue + date field + replay chip + button) forces
          horizontal scroll on the whole page — the "not responsive"
          report of 9/16, introduced with the date selector. */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', rowGap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', rowGap: 1, minWidth: 0 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Strategy Bots
          </Typography>
          <Tooltip
            title={
              streamStatus === 'open'
                ? 'Live — changes from Alpaca appear automatically'
                : streamStatus === 'connecting'
                ? 'Connecting to live updates…'
                : 'Live updates offline — data may be stale'
            }
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FiberManualRecord
                sx={{
                  fontSize: 12,
                  color:
                    streamStatus === 'open'
                      ? 'success.main'
                      : streamStatus === 'connecting'
                      ? 'warning.main'
                      : 'text.disabled',
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {streamStatus === 'open' ? 'Live' : streamStatus}
              </Typography>
            </Box>
          </Tooltip>
          {venue && (
            <Chip
              label={venue.toUpperCase()}
              size="small"
              color={venue === 'live' ? 'primary' : 'default'}
              variant={venue === 'live' ? 'filled' : 'outlined'}
            />
          )}
          <TextField
            id="bots-view-date"
            type="date"
            size="small"
            value={viewDate}
            inputProps={{ max: etToday(), style: { fontSize: 13, padding: '6px 8px' } }}
            onChange={(e) => {
              if (e.target.value) setViewDate(e.target.value);
            }}
            // Native date inputs only open their picker from the tiny
            // calendar glyph (near-invisible on the dark theme) — make
            // the WHOLE field open it. showPicker needs a user gesture
            // and a secure context; both hold here.
            onClick={(e) => {
              const input = (e.currentTarget as HTMLElement).querySelector('input');
              try { (input as any)?.showPicker?.(); } catch { /* focus fallback */ }
            }}
            sx={{
              cursor: 'pointer',
              width: { xs: 155, sm: 'auto' },
              '& input': { cursor: 'pointer' },
              '& input::-webkit-calendar-picker-indicator': {
                cursor: 'pointer', opacity: 0.85,
                filter: (theme) =>
                  theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
              },
            }}
          />
          {!isToday && (
            <Button size="small" onClick={() => setViewDate(etToday())}>
              Today
            </Button>
          )}
          {!liveView && (
            <Chip
              label={`replay · ${isToday ? 'today (market closed)' : viewDate}`}
              size="small"
              color={isToday ? 'default' : 'warning'}
              variant="outlined"
            />
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<AddCircleOutline />}
          component={RouterLink}
          to="/strategy/covered-call/new"
        >
          New Covered Call
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Bot list */}
        <Box sx={{ flex: selectedBot ? 1 : 'none', minWidth: 0, width: selectedBot ? undefined : '100%' }}>
          {/* Active bots */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Active Bots ({activeBots.length})
              </Typography>

              {activeBots.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <SmartToy sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                    No active bots
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click "New Covered Call" to get started.
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gap: 1.5,
                    // one card per row, always — the P&L chart needs the
                    // full width to be readable (user call, 9/15)
                    gridTemplateColumns: '1fr',
                  }}
                >
                  {activeBots.map((bot) => (
                    <BotCard
                      key={bot.id}
                      bot={bot}
                      mark={liveView ? marks[bot.id] : undefined}
                      seriesPoints={series[bot.id]}
                      dayKey={viewDate}
                      signal={signals[bot.id]}
                      dayRealized={realized[bot.id]}
                      dayFees={fees[bot.id]}
                      trailEvents={trails[bot.id]}
                      condor={condors[bot.id]}
                      live={liveView}
                      selected={selectedBot?.id === bot.id}
                      stopping={stopping === bot.id}
                      onClick={() => fetchBotDetail(bot)}
                      onStop={() => setConfirmStop(bot)}
                    />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Inactive bots */}
          {inactiveBots.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
                  Inactive ({inactiveBots.length})
                </Typography>
                <Box sx={{ display: 'grid', gap: 1.5 }}>
                  {inactiveBots.map((bot) => (
                    <BotCard
                      key={bot.id}
                      bot={bot}
                      selected={selectedBot?.id === bot.id}
                      stopping={false}
                      onClick={() => fetchBotDetail(bot)}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Event timeline */}
        {selectedBot && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {selectedBot.symbol} — Events
                  </Typography>
                  <Chip
                    label={selectedBot.holding
                      && (STATE_LABELS[selectedBot.state] || '') === 'Scanning'
                      ? 'Holding'
                      : (STATE_LABELS[selectedBot.state] || selectedBot.state)}
                    size="small"
                    color={selectedBot.holding
                      ? 'success'
                      : (STATE_COLORS[selectedBot.state] || 'default')}
                  />
                </Box>

                {loadingEvents ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : events.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No events recorded yet.
                  </Typography>
                ) : (
                  <List disablePadding>
                    {events.map((ev, idx) => (
                      <React.Fragment key={ev.id}>
                        <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              mr: 2,
                              mt: 0.5,
                              bgcolor: ev.type === 'ERROR' ? 'error.main' : 'primary.main',
                            }}
                          >
                            {EVENT_ICONS[ev.type] || <SmartToy fontSize="small" />}
                          </Avatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip label={ev.type} size="small" variant="outlined" />
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(ev.createdAt).toLocaleString()}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              ev.payload ? (
                                <Box sx={{ mt: 0.5 }}>
                                  {Object.entries(ev.payload).map(([k, v]) => (
                                    <Typography key={k} variant="caption" display="block" color="text.secondary">
                                      {k}: <strong>{String(v)}</strong>
                                    </Typography>
                                  ))}
                                </Box>
                              ) : null
                            }
                          />
                        </ListItem>
                        {idx < events.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>
      <Dialog open={confirmStop != null} onClose={() => setConfirmStop(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Stop {confirmStop ? `${STRATEGY_LABELS[confirmStop.strategy] || confirmStop.strategy} · ${confirmStop.symbol}` : 'bot'}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This closes any open legs at market, cancels pending orders and turns recurrence off.
            The bot will not trade again until it is re-armed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmStop(null)}>Keep running</Button>
          <Button color="error" variant="contained" onClick={() => { const id = confirmStop!.id; setConfirmStop(null); handleStop(id); }}>
            Stop bot
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ---- BotCard sub-component -------------------------------------------------

interface BotCardProps {
  bot: Bot;
  mark?: LiveMark;
  seriesPoints?: MarkPoint[];
  /** The viewed ET day — playback resets when it changes. */
  dayKey?: string;
  signal?: TodaySignal | null;
  dayRealized?: number | null;
  dayFees?: DayFees | null;
  /** IC: the day's TRAIL_ARMED / TRAIL_FLOOR events (/marks dayTrail). */
  trailEvents?: TrailEvent[] | null;
  /** IC: the cycle's strikes (/marks dayCondor) for the live readout. */
  condor?: DayCondor | null;
  /** false when replaying a past day — holding/live UI is suppressed. */
  live?: boolean;
  selected: boolean;
  stopping: boolean;
  onClick: () => void;
  onStop?: () => void;
}

/** Red/green mega-cap map from a breadth-signal payload. */
const SignalLine: React.FC<{ signal: TodaySignal }> = ({ signal }) => (
  <Box sx={{ mt: 0.75 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      <strong>{signal.action || '—'}</strong>
      {signal.breadth != null && ` · breadth ${signal.breadth}/8`}
      {signal.reds != null && ` · greens ${8 - signal.reds}/8`}
      {signal.regimeGate != null &&
        ` · gate ${signal.regimeGate.pass ? 'pass' : 'FAIL'}`}
      {signal.refPrice != null && ` · ref ${Number(signal.refPrice).toFixed(2)}`}
      {signal.feed && ` · ${String(signal.feed).toUpperCase()}`}
      {signal.icEnteredToday != null && ` · IC ${signal.icEnteredToday ? 'in' : 'out'}`}
    </Typography>
    {signal.redMap && (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
        {Object.entries(signal.redMap).map(([sym, red]) => (
          <Chip
            key={sym}
            label={sym}
            size="small"
            variant="outlined"
            color={red ? 'error' : 'success'}
            sx={{ height: 18, fontSize: 10, fontFamily: 'monospace' }}
          />
        ))}
      </Box>
    )}
  </Box>
);

const fmtUsd = (v: number) =>
  `${v < 0 ? '-' : '+'}$${Math.abs(v).toFixed(0)}`;

/** Live position-value line: unrealized P&L, % of credit, and progress
 *  between the stop line and the profit target. Dims when the last mark
 *  is older than ~3 minutes (market closed, or stream hiccup). */
const LiveValueLine: React.FC<{ mark: LiveMark }> = ({ mark }) => {
  const ageMs = Date.now() - mark.receivedAt;
  const stale = ageMs > 3 * 60_000;
  const pnl = mark.unrealizedPnl;
  const color = pnl >= 0 ? 'success.main' : 'error.main';
  // Position of current P&L between stop (0%) and target (100%).
  const span = mark.targetPnl - mark.stopPnl;
  const frac = span > 0 ? Math.min(1, Math.max(0, (pnl - mark.stopPnl) / span)) : 0;
  const asOf = new Date(mark.at).toLocaleTimeString([], {
    hour: '2-digit', minute: '2-digit',
  });
  return (
    <Tooltip
      title={
        `Close cost ${fmtUsd(-mark.closeCost)} vs credit ${fmtUsd(mark.credit)}` +
        ` · target ${fmtUsd(mark.targetPnl)} · stop ${fmtUsd(mark.stopPnl)}` +
        (mark.ratchetArmed ? ' · ratchet armed (breakeven floor)' : '') +
        (mark.trailArmed
          ? ` · trailing after the 50% target${mark.trailFloorPnl != null
            ? `: floor ${fmtUsd(mark.trailFloorPnl)} under peak ${fmtUsd(mark.trailPeakPnl ?? 0)}` : ''}`
          : '')
      }
    >
      <Box sx={{ mt: 0.75, opacity: stale ? 0.5 : 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color }}>
            {fmtUsd(pnl)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {mark.pnlPctOfCredit >= 0 ? '+' : ''}{mark.pnlPctOfCredit.toFixed(0)}% of credit
          </Typography>
          {mark.ratchetArmed && (
            <Chip label="ratchet armed" size="small" color="info" variant="outlined" />
          )}
          <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
            {stale ? `as of ${asOf}` : asOf}
          </Typography>
        </Box>
        <Box sx={{ mt: 0.5, height: 4, borderRadius: 2, bgcolor: 'action.hover', position: 'relative' }}>
          <Box
            sx={{
              position: 'absolute', left: 0, top: 0, bottom: 0,
              width: `${frac * 100}%`, borderRadius: 2,
              bgcolor: pnl >= 0 ? 'success.main' : 'error.main',
              transition: 'width 0.6s ease',
            }}
          />
        </Box>
      </Box>
    </Tooltip>
  );
};

export const BotCard: React.FC<BotCardProps> = ({
  bot, mark, seriesPoints, dayKey, signal, dayRealized, dayFees, trailEvents, condor,
  live = true, selected, stopping, onClick, onStop,
}) => {
  // Per-card tape playback (replay mode only): each strategy's day is
  // its own story — the IC's crush, the spread's dip, the put's grind
  // — so every card carries its own transport instead of one global
  // head (user correction 9/16). playT in ET hours; null = full day.
  const [playT, setPlayT] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [tapeMinPerSec, setTapeMinPerSec] = useState(5);
  useEffect(() => { setPlayT(null); setPlaying(false); }, [dayKey, live]);
  useEffect(() => {
    if (!playing) return;
    const t = setInterval(() => {
      setPlayT((prev) => {
        const next = (prev ?? 9.5) + tapeMinPerSec / 600; // 100ms tick
        if (next >= 16.05) { setPlaying(false); return 16.05; }
        return next;
      });
    }, 100);
    return () => clearInterval(t);
  }, [playing, tapeMinPerSec]);
  const fmtHead = (h: number) => {
    const hh = Math.floor(h); const mm = Math.floor((h - hh) * 60);
    return `${hh}:${String(mm).padStart(2, '0')}`;
  };
  const replayable = !live && (seriesPoints?.length ?? 0) >= 2;
  const isActive = !['STOPPED', 'ERROR'].includes(bot.state);
  const isBreadth = BREADTH_STRATEGIES.has(bot.strategy);
  const isIc = bot.strategy === 'IRON_CONDOR';
  // Latest underlying price the marks carry — the readout's fallback.
  const lastRef = (() => {
    for (let i = (seriesPoints?.length ?? 0) - 1; i >= 0; i--) {
      const p = seriesPoints![i];
      if (p.refLast != null) return { price: p.refLast, at: p.at };
    }
    return null;
  })();
  // The trail chip reads the latest mark (live) or the series tail.
  const tailMark = (mark ?? (seriesPoints?.length ? seriesPoints[seriesPoints.length - 1] : undefined)) as
    (MarkPoint & { trailFloorPnl?: number | null }) | undefined;
  // "Holding" beats the lifetime state label while a position is on.
  // The server flag is authoritative when present — an exit fill fires
  // trade_filled over the stream, the list refetches, and the flag
  // clears within ~a second. The fresh-mark heuristic is only a
  // fallback for servers that don't send the flag yet; never let it
  // overrule an explicit holding=false after an exit.
  const holding = live && (
    bot.holding === true ||
    (bot.holding === undefined &&
      mark != null && Date.now() - mark.receivedAt < 3 * 60_000));
  const latestPnl = mark?.unrealizedPnl
    ?? (seriesPoints?.length ? seriesPoints[seriesPoints.length - 1].unrealizedPnl : null);
  const pnlColor = latestPnl == null
    ? 'text.secondary'
    : latestPnl >= 0 ? 'success.main' : 'error.main';
  // Scrubbing/playing a replay: the headline number follows the
  // playhead — the mark as of that moment of the day, not the day's
  // ending value (user request 9/17).
  const atH = (iso: string) =>
    Number(iso.slice(11, 13)) + Number(iso.slice(14, 16)) / 60
    + (Number(iso.slice(17, 19)) || 0) / 3600;
  // Replay: the playhead in ET hours (null = full day), and the mark in
  // effect there — everything replayed (headline, chip, readout, trail)
  // reads this one mark, so the card is the card as it was at that minute.
  const headH = !live ? (playT ?? 24) : null;
  const headMark = (!live && seriesPoints?.length)
    ? (() => {
        let v: MarkPoint | null = null;
        for (const p of seriesPoints) {
          if (atH(p.at) <= headH!) v = p;
          else break;
        }
        return v;
      })()
    : null;
  const headPnl = (!live && playT != null) ? (headMark?.unrealizedPnl ?? null) : null;
  // The day's first mark with a price — the replay readout's reference.
  const firstRef = (() => {
    for (const p of seriesPoints || []) {
      if (p.refLast != null) return { price: p.refLast, at: p.at };
    }
    return null;
  })();
  // IC trail as of the playhead (REBUILT from the marks/events at or
  // before it, never clipped from live state); whole day when live.
  const trailModel = useMemo(
    () => (isIc && seriesPoints?.length)
      ? buildTrailUpTo(seriesPoints, trailEvents, headH)
      : null,
    [isIc, seriesPoints, trailEvents, headH],
  );
  // Header chip: live reads the latest mark; replay reads the model at the
  // playhead (not armed before the arm, cleared once the exit is reached).
  const trailChip = live
    ? (tailMark?.trailArmed
        ? { floor: tailMark.trailFloorPnl ?? null }
        : null)
    : (trailModel && !trailModel.exitAt
        ? { floor: trailModel.steps[trailModel.steps.length - 1]?.floor ?? null }
        : null);

  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        borderColor: selected ? 'primary.main' : undefined,
        borderWidth: selected ? 2 : 1,
        opacity: isActive ? 1 : 0.6,
        '&:hover': { borderColor: 'primary.light' },
      }}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        {/* header: strategy · symbol · state · ratchet · stop */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {STRATEGY_LABELS[bot.strategy] || bot.strategy}
            <Typography component="span" variant="subtitle1" color="text.secondary">
              {' '}{bot.symbol}
            </Typography>
          </Typography>
          <Chip
            label={holding && (STATE_LABELS[bot.state] || '') === 'Scanning'
              ? 'Holding' : (STATE_LABELS[bot.state] || bot.state)}
            size="small"
            color={holding ? 'success' : (STATE_COLORS[bot.state] || 'default')}
          />
          {mark?.ratchetArmed && (
            <Chip label="ratchet armed" size="small" color="warning" />
          )}
          {isIc && trailChip && (
            <Chip
              label={trailChip.floor != null
                ? `trailing · floor ${fmtUsd(trailChip.floor)}` : 'trailing'}
              size="small"
              color="secondary"
            />
          )}
          {isActive && onStop && (
            <Button
              size="small"
              color="error"
              variant="outlined"
              sx={{ ml: 'auto' }}
              startIcon={stopping ? <CircularProgress size={14} /> : <StopCircle />}
              disabled={stopping}
              onClick={(e) => {
                e.stopPropagation();
                onStop();
              }}
            >
              Stop
            </Button>
          )}
        </Box>

        {/* P&L: live mark while holding, the day's realized once closed.
            With fee data (live venue) the headline is NET so it matches
            the broker; the caption itemizes the fees and their basis. */}
        {!live && playT != null ? (
          <Box sx={{ mt: 0.5 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700,
                    color: headPnl == null ? 'text.secondary'
                      : headPnl >= 0 ? 'success.main' : 'error.main' }}
            >
              {headPnl == null ? '—' : fmtUsd(headPnl)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {headPnl == null
                ? `at ${fmtHead(playT)} · no position yet`
                : `at ${fmtHead(playT)} · replay`}
            </Typography>
          </Box>
        ) : isActive && !holding && dayRealized != null ? (
          <Box sx={{ mt: 0.5 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700,
                    color: (dayRealized - (dayFees?.amount ?? 0)) >= 0
                      ? 'success.main' : 'error.main' }}
            >
              {fmtUsd(dayRealized - (dayFees?.amount ?? 0))}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dayFees
                ? `net of ${fmtUsd(dayFees.amount)} fees`
                  + `${dayFees.basis === 'estimated' ? ' (est.)'
                       : dayFees.basis === 'apportioned' ? ' (apport.)' : ''}`
                  + ` · ${fmtUsd(dayRealized)} gross`
                : 'realized · trade closed'}
            </Typography>
          </Box>
        ) : (
          isActive && latestPnl != null && (
            <Typography variant="h5" sx={{ fontWeight: 700, color: pnlColor, mt: 0.5 }}>
              {fmtUsd(latestPnl)}
            </Typography>
          )
        )}
        {isActive && holding && mark && <LiveValueLine mark={mark} />}
        {/* IC: underlying + distance to the short strikes. Live = the quote
            feed; replay = the mark at the playhead, no feed. */}
        {isIc && isActive && (
          <Box onClick={(e) => e.stopPropagation()}>
            {live ? (
              <UnderlyingQuote symbol={bot.symbol} condor={condor} fallback={lastRef} />
            ) : (
              <UnderlyingReplay
                symbol={bot.symbol}
                condor={condor}
                head={headMark ? { price: headMark.refLast ?? null, at: headMark.at } : null}
                first={firstRef}
                headLabel={playT != null ? fmtHead(playT) : null}
              />
            )}
          </Box>
        )}

        {/* intraday P&L chart — the pattern-discovery view */}
        {isActive && (
          <Box sx={{ mt: 1 }} onClick={(e) => e.stopPropagation()}>
            <BotPnlChart
              points={(seriesPoints || []) as MarkPoint[]}
              cutoffH={!live ? playT : null}
              height={isBreadth ? 170 : 210}
              trail={trailModel}
            />
            {replayable && (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap' }}
                onClick={(e) => e.stopPropagation()}
              >
                <IconButton
                  size="small"
                  onClick={() => {
                    if (playing) { setPlaying(false); return; }
                    setPlayT((prev) => (prev == null || prev >= 16.0 ? 9.5 : prev));
                    setPlaying(true);
                  }}
                  aria-label={playing ? 'pause replay' : 'play replay'}
                >
                  {playing ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
                </IconButton>
                <Slider
                  size="small"
                  min={9.5}
                  max={16.05}
                  step={1 / 60}
                  value={playT ?? 16.05}
                  onChange={(_, v) => setPlayT(v as number)}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => fmtHead(v as number)}
                  sx={{ flex: 1, maxWidth: 260, minWidth: 110 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', minWidth: 40 }}>
                  {playT != null ? fmtHead(playT) : 'full'}
                </Typography>
                <Chip
                  label={`${tapeMinPerSec}m/s`}
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    setTapeMinPerSec((s) => (s === 5 ? 15 : s === 15 ? 2 : 5))}
                  sx={{ cursor: 'pointer' }}
                />
                {playT != null && (
                  <Button
                    size="small"
                    onClick={() => { setPlaying(false); setPlayT(null); }}
                    sx={{ minWidth: 0, px: 1, fontSize: 11 }}
                  >
                    full day
                  </Button>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* breadth bots: today's signal verdict + red map */}
        {isBreadth && signal && <SignalLine signal={signal} />}

        {/* position details */}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {bot.contractsTarget != null && bot.contractsTarget > 0 &&
            `${bot.contractsTarget} contract${bot.contractsTarget > 1 ? 's' : ''}`}
          {bot.currentStrike != null && ` · $${Number(bot.currentStrike).toFixed(2)} strike`}
          {bot.currentExpiration && ` · exp ${bot.currentExpiration}`}
        </Typography>
        {bot.currentOptionSymbol && (
          <Typography variant="caption" color="text.secondary">
            {bot.currentOptionSymbol}
          </Typography>
        )}
        {bot.sharesHeld != null && bot.sharesHeld > 0 && (
          <Typography variant="caption" color="text.secondary" display="block">
            {bot.sharesHeld} shares held
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default Robot;
